import { FishingReport, Product, WaterbodyDetail } from '@omniafishing/core';
import React, { useState } from 'react';
import { ContentWrapper } from '../../../content_wrapper/content_wrapper';
import {
  SVGBackgroundClipPathDesktop,
  SVGBackgroundClipPathMobile,
} from '../../images/background_clip_paths';
import { FishingReportCard } from '../../../fishing_report_card/fishing_report_card';
import { LINE_ATTRIBUTION } from '../../../../redux/cart';
import { useResponsive } from '../../../../hooks/use_responsive';
import styles from './waterbody_detail_fishing_reports_overview.less';
import { OmniaLinkButton } from '../../../omnia_button/omnia_link_button';
import { OmniaUrls } from '../../../../lib/urls';
import { useDispatch } from 'react-redux';
import { FishingReportModalActions } from '../../../../redux/fishing_report_modal';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import {
  SORT_OPTIONS,
  SortOptions,
  sortOptions,
  tooltipContent,
  tooltipTitle,
} from '../../../../routes/waterbody_detail/waterbody_fishing_reports_page';
import { Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAfterMountedEffect } from '../../../../hooks/use_after_mounted_effect';
import { apiV1 } from '../../../../lib/api';

interface WaterbodyDetailFishingReportsOverviewsProps {
  fishingReportsNearby: FishingReport[];
  initialFishingReports: FishingReport[];
  productToShowInFollowModal: Product;
  totalFishingReportsCount: number;
  waterbody: WaterbodyDetail;
}

export const NUMBER_OF_REPORTS_TO_INCREMENT = 6;
export const INITIAL_REPORTS_TO_SHOW = 3;

export const WaterbodyDetailFishingReportsOverview = (
  props: WaterbodyDetailFishingReportsOverviewsProps
) => {
  const { initialFishingReports, fishingReportsNearby, waterbody, totalFishingReportsCount } =
    props;

  const dispatch = useDispatch();
  const { isSmallDesktop, isMobile, isDesktop } = useResponsive();
  const { primary_name, url_slug } = waterbody;
  const hasFishingReports = totalFishingReportsCount > 0;
  const noFishingReports = !hasFishingReports;
  const hasNearbyFishingReports = fishingReportsNearby.length > 0;
  const noNearbyFishingReports = !hasNearbyFishingReports;

  let heroImageWidth: number;
  if (isSmallDesktop) {
    heroImageWidth = 435;
  } else if (!isSmallDesktop && !isMobile) {
    heroImageWidth = 478;
  }

  const [fishingReports, setFishingReports] = useState<FishingReport[]>(initialFishingReports);
  const [sortBy, setSortBy] = useState<SortOptions>(SORT_OPTIONS.MOST_RELEVANT);
  const onSortChange = (val: SortOptions) => {
    setSortBy(val);
  };

  const fetchFishingReports = async () => {
    try {
      const res = await apiV1.waterbodyFishingReportsFetch(url_slug, {
        sort: sortBy,
        per_page: INITIAL_REPORTS_TO_SHOW,
      });
      setFishingReports(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  useAfterMountedEffect(() => {
    fetchFishingReports();
  }, [sortBy]);

  const nearbyReportsToRender = fishingReportsNearby.slice(0, INITIAL_REPORTS_TO_SHOW);
  const nearbyReportsRemaining = fishingReportsNearby.length - INITIAL_REPORTS_TO_SHOW;
  const fishingReportsToRender = fishingReports.slice(0, INITIAL_REPORTS_TO_SHOW);
  const totalReportCountRemaining = totalFishingReportsCount - INITIAL_REPORTS_TO_SHOW;
  return (
    <div className={styles.fullWidthContainer}>
      <ContentWrapper>
        <div className={styles.sectionHeading}>
          <div className={styles.header}>
            <h2>
              Fishing Reports {noFishingReports && `Near ${primary_name}`}{' '}
              {isMobile && hasFishingReports && `(${totalFishingReportsCount})`}
            </h2>
            {isDesktop && (
              <span>
                <strong>{totalFishingReportsCount} Reports</strong> on {primary_name}
              </span>
            )}
          </div>
          {hasFishingReports && (
            <div className={styles.radiosSectionWrapper}>
              <p className={styles.filterSortByTag}>
                <strong>Sort by</strong>
              </p>
              <div className={styles.radios}>
                <div className={styles.radioGroup}>
                  <label
                    htmlFor={sortOptions[0].label}
                    className={styles.radioContainer}
                    onClick={() => onSortChange(sortOptions[0].value)}
                  >
                    <input
                      type="radio"
                      name={sortOptions[0].label}
                      value={sortOptions[0].value}
                      checked={sortBy === sortOptions[0].value}
                    />
                    <span className={styles.radioCustom} />
                    <span className={styles.radioLabel}>{sortOptions[0].label}</span>
                  </label>
                  <div className={styles.tooltip}>
                    <Popover title={tooltipTitle} content={tooltipContent}>
                      <InfoCircleOutlined />
                    </Popover>
                  </div>
                  <label
                    htmlFor={sortOptions[1].label}
                    className={styles.radioContainer}
                    onClick={() => onSortChange(sortOptions[1].value)}
                  >
                    <input
                      type="radio"
                      name={sortOptions[1].label}
                      value={sortOptions[1].value}
                      checked={sortBy === sortOptions[1].value}
                    />

                    <span className={styles.radioLabel}>{sortOptions[1].label}</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.reportsContainer}>
          {noFishingReports
            ? fishingReportsNearby.length > 0 && (
                <ul className={styles.frRows}>
                  {nearbyReportsToRender.map((report, i) => {
                    return (
                      <li key={report.id} className={styles.card}>
                        <FishingReportCard
                          fishingReport={report}
                          lineItemAttribution={[
                            {
                              key: LINE_ATTRIBUTION.WATERBODY_ID,
                              value: waterbody.id,
                            },
                          ]}
                          position={i}
                          className={styles.fishingReportCard}
                          alternateHeroPixelWidth={heroImageWidth}
                          reportedOnBadge
                        />
                      </li>
                    );
                  })}
                </ul>
              )
            : hasFishingReports && (
                <ul className={styles.fishingReports}>
                  {fishingReportsToRender.map((report, i) => {
                    return (
                      <li key={report.id} className={styles.card}>
                        <FishingReportCard
                          fishingReport={report}
                          lineItemAttribution={[
                            {
                              key: LINE_ATTRIBUTION.WATERBODY_ID,
                              value: report.waterbody.id,
                            },
                          ]}
                          position={i}
                          showLakeName={false}
                          userImageSize={58}
                          alternateHeroPixelWidth={heroImageWidth}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}

          {noNearbyFishingReports && noFishingReports && (
            <div className={styles.noneFishingReports}>
              <h3>We didn't find any recent fishing reports on nearby lakes</h3>
              <p>Be the first to file a fishing report on {primary_name}</p>
              <OmniaButton
                kind="tertiary"
                size="lg"
                onClick={() => {
                  dispatch(FishingReportModalActions.FISHING_REPORT_MODAL_OPEN(waterbody));
                }}
              >
                File a Fishing Report
              </OmniaButton>
            </div>
          )}
          {(hasNearbyFishingReports || totalFishingReportsCount > 3) && (
            <OmniaLinkButton
              kind="tertiary"
              size="lg"
              to={`${OmniaUrls.waterbody(waterbody)}/fishing-reports`}
              className={styles.viewMoreReportsButton}
            >
              View More Fishing Reports (
              {hasNearbyFishingReports ? nearbyReportsRemaining : totalReportCountRemaining})
            </OmniaLinkButton>
          )}
        </div>
      </ContentWrapper>
      {/* these svg's work with the clip paths .svgWave in the associated less file.
      they are connected using id's */}
      <div className={styles.backgroundSVGs}>
        {SVGBackgroundClipPathDesktop}
        {SVGBackgroundClipPathMobile}
      </div>
      <div className={styles.svgWave}></div>
    </div>
  );
};
